import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { ageGroupsAPI } from '../../services/api';
import { AgeGroup, CreateAgeGroupRequest, UpdateAgeGroupRequest } from '../../types/ageGroup';
import { getErrorMessage } from '../../utils/errorMessage';

interface AgeGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ageGroup: AgeGroup | null;
}

const AgeGroupModal: React.FC<AgeGroupModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  ageGroup,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    birthDateFrom: '',
    birthDateTo: '',
    sortOrder: 0,
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEdit = ageGroup !== null;

  useEffect(() => {
    if (ageGroup) {
      setFormData({
        name: ageGroup.name,
        birthDateFrom: ageGroup.birthDateFrom.split('T')[0],
        birthDateTo: ageGroup.birthDateTo.split('T')[0],
        sortOrder: ageGroup.sortOrder,
        isActive: ageGroup.isActive,
      });
    } else {
      setFormData({
        name: '',
        birthDateFrom: '',
        birthDateTo: '',
        sortOrder: 0,
        isActive: true,
      });
    }
    setError('');
  }, [ageGroup, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEdit) {
        const updateData: UpdateAgeGroupRequest = {
          name: formData.name,
          birthDateFrom: formData.birthDateFrom,
          birthDateTo: formData.birthDateTo,
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
        };
        await ageGroupsAPI.update(ageGroup!.id, updateData);
      } else {
        const createData: CreateAgeGroupRequest = {
          name: formData.name,
          birthDateFrom: formData.birthDateFrom,
          birthDateTo: formData.birthDateTo,
          sortOrder: formData.sortOrder,
        };
        await ageGroupsAPI.create(createData);
      }
      onSuccess();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Hiba történt a mentés során.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Korosztály szerkesztése' : 'Új korosztály'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Név <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="pl. U10"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">A névnek egyedinek kell lennie</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="birthDateFrom" className="block text-sm font-medium text-gray-700">
              Születési dátum tól <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="birthDateFrom"
              name="birthDateFrom"
              required
              value={formData.birthDateFrom}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">A korábbi dátum → az idősebbek</p>
          </div>
          <div>
            <label htmlFor="birthDateTo" className="block text-sm font-medium text-gray-700">
              Születési dátum ig <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="birthDateTo"
              name="birthDateTo"
              required
              value={formData.birthDateTo}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">A későbbi dátum → a fiatalabbak</p>
          </div>
        </div>

        <div>
          <label htmlFor="sortOrder" className="block text-sm font-medium text-gray-700">
            Sorrend
          </label>
          <input
            type="number"
            id="sortOrder"
            name="sortOrder"
            value={formData.sortOrder}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">Kisebb szám = előrébb jelenik meg</p>
        </div>

        {isEdit && (
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
              Aktív
            </label>
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 pt-4 md:flex-row md:justify-end md:gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full md:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {loading ? 'Mentés...' : isEdit ? 'Mentés' : 'Létrehozás'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AgeGroupModal;
